import moment from "moment";
import { formatNumberWithCommas } from "../../../config/utils";
import { isSuperAdminRole } from "../../../constants/roleConstants";

export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const PNL_STATUS = { FINAL: 1, PROVISIONAL: 2, IN_PROGRESS: 3, AWAITING_RETURNS: 4 };
export const SHIPPING_LABELS = "ShippingLabels";
export const DETAIL_LINE_LIMIT = 100;

const KNOWN_CATEGORIES = new Set(["Tasks", "CarrierConnect", "Invoices", SHIPPING_LABELS]);
const YEAR_OPTION_COUNT = 3;
const DEFAULT_ERROR_MESSAGE = "Unable to load the Profit & Loss report.";

const toAmount = (value) => Number(value) || 0;
const round2 = (value) => Math.round(value * 100) / 100;
const padMonth = (month) => String(month).padStart(2, "0");
const monthKey = (row) => `${row.year}-${padMonth(row.month)}`;

export const monthLabel = (year, month) => `${MONTH_NAMES[month - 1]} ${year}`;

// The report is Super Admin only; the backend answers 403 for everyone else.
export const isPnlReportUser = (user) => Boolean(user?.isSuperAdmin) || isSuperAdminRole(user);

// "CarrierConnect" -> "Carrier connect"; new backend categories get a readable label automatically.
export const getCategoryLabel = (category) => {
  const spaced = String(category ?? "").replaceAll(/([a-z])([A-Z])/g, "$1 $2").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : "";
};

export const formatMoney = (value) => {
  const amount = round2(toAmount(value));
  return `${amount < 0 ? "-" : ""}$${formatNumberWithCommas(Math.abs(amount))}`;
};

export const formatLockDate = (value) => {
  const parsed = moment.utc(value);
  return value && parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
};

export const getErrorMessage = (body) =>
  body?.Message?.AppStatusDescription || body?.message || DEFAULT_ERROR_MESSAGE;

// Never rejects: resolves to { rows, error } so callers only branch on the error text.
export const loadEntityList = async (request) => {
  try {
    const body = (await request())?.data;
    if (body?.Status === 200) return { rows: body.EntityList || [], error: "" };
    return { rows: [], error: getErrorMessage(body) };
  } catch (error) {
    return { rows: [], error: getErrorMessage(error?.response?.data) };
  }
};

export const toggleKey = (keys, key) => {
  const next = new Set(keys);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export const getStatusInfo = (row) => {
  switch (row?.statusCode) {
    case PNL_STATUS.FINAL:
      return { kind: "final", label: "Final" };
    case PNL_STATUS.PROVISIONAL: {
      const lockDate = formatLockDate(row.lockDate);
      return { kind: "prov", label: lockDate ? `Provisional, locks ${lockDate}` : "Provisional" };
    }
    case PNL_STATUS.IN_PROGRESS:
      return { kind: "live", label: "In progress" };
    case PNL_STATUS.AWAITING_RETURNS:
      return { kind: "prov", label: "Provisional, awaiting returns" };
    default:
      return { kind: "live", label: row?.status || "In progress" };
  }
};

export const getMonthStatus = (rows) => {
  const kinds = new Set(rows.map((row) => getStatusInfo(row).kind));
  if (kinds.has("live")) return { kind: "live", label: "In progress" };
  if (kinds.has("prov")) return { kind: "prov", label: "Partially final" };
  return { kind: "final", label: "Final" };
};

export const getLockText = (row) => {
  const lockDate = formatLockDate(row?.lockDate);
  switch (row?.statusCode) {
    case PNL_STATUS.FINAL:
      return lockDate
        ? `Locked on ${lockDate}. Refresh will not change these values.`
        : "Locked. Refresh will not change these values.";
    case PNL_STATUS.PROVISIONAL:
      return lockDate
        ? `Open for corrections until ${lockDate}. Refresh recalculates this row.`
        : "Open for corrections. Refresh recalculates this row.";
    case PNL_STATUS.AWAITING_RETURNS:
      return "Waiting for carrier refunds and adjustments. Values update until this month's shipping-label returns are closed.";
    default:
      return lockDate
        ? `Current month. Values update on each refresh. Locks on ${lockDate}.`
        : "Current month. Values update on each refresh until month end.";
  }
};

export const netClass = (value) => (toAmount(value) < 0 ? "neg" : "");

// Months and the current month are evaluated in UTC because the backend buckets by UTC.
export const getYearOptions = (now = new Date()) =>
  Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => now.getUTCFullYear() - index);

export const getMonthOptions = (year, now = new Date()) => {
  const lastMonth = Number(year) === now.getUTCFullYear() ? now.getUTCMonth() + 1 : 12;
  return Array.from({ length: lastMonth }, (_, index) => ({
    value: index + 1,
    label: monthLabel(year, index + 1),
  }));
};

export const getDefaultFilters = (now = new Date()) => ({
  year: now.getUTCFullYear(),
  fromMonth: 1,
  toMonth: now.getUTCMonth() + 1,
  categories: [],
  subTypes: [],
});

export const applyFilterChange = (filters, field, rawValue, now = new Date()) => {
  const isNumeric = ["year", "fromMonth", "toMonth"].includes(field);
  const next = { ...filters, [field]: isNumeric ? Number(rawValue) : rawValue };
  if (field === "year") {
    next.fromMonth = 1;
    next.toMonth = getMonthOptions(next.year, now).length;
  }
  return next;
};

export const validateFilters = (filters) =>
  filters.fromMonth > filters.toMonth ? "From month cannot be after To month." : "";

// An empty selection means "everything", for both the API and the client-side filter.
const matchesSelection = (selected, value) => selected.length === 0 || selected.includes(value);

export const hasSelection = (filters) => filters.categories.length > 0 || filters.subTypes.length > 0;

export const filterRows = (rows, categories, subTypes) =>
  rows.filter((row) => matchesSelection(categories, row.category) && matchesSelection(subTypes, row.subType));

export const getCategoryOptions = (rows) => {
  const extras = [...new Set(rows.map((row) => row.category))].filter(
    (category) => category && !KNOWN_CATEGORIES.has(category)
  );
  return [...KNOWN_CATEGORIES, ...extras].map((value) => ({ value, label: getCategoryLabel(value) }));
};

export const getTypeOptions = (rows, categories) => {
  const types = rows.filter((row) => matchesSelection(categories, row.category)).map((row) => row.subType);
  return [...new Set(types)].filter(Boolean).sort((a, b) => a.localeCompare(b));
};

// Drops selected types that no longer belong to the selected categories.
export const pruneSubTypes = (filters, optionRows) => {
  const allowed = new Set(getTypeOptions(optionRows, filters.categories));
  return { ...filters, subTypes: filters.subTypes.filter((type) => allowed.has(type)) };
};

export const sumTotals = (rows) => {
  const totals = rows.reduce(
    (acc, row) => ({
      gross: acc.gross + toAmount(row.grossAmount),
      cost: acc.cost + toAmount(row.costAmount),
      adjustment: acc.adjustment + toAmount(row.adjustmentAmount),
      net: acc.net + toAmount(row.netAmount),
    }),
    { gross: 0, cost: 0, adjustment: 0, net: 0 }
  );
  return {
    gross: round2(totals.gross),
    cost: round2(totals.cost),
    adjustment: round2(totals.adjustment),
    net: round2(totals.net),
  };
};

// Keeps the API order inside a month but makes each category contiguous.
const groupByCategory = (rows) => {
  const byCategory = new Map();
  rows.forEach((row) => {
    if (!byCategory.has(row.category)) byCategory.set(row.category, []);
    byCategory.get(row.category).push(row);
  });
  return [...byCategory.values()].flat();
};

export const groupRowsByMonth = (rows) => {
  const byMonth = new Map();
  rows.forEach((row) => {
    const key = monthKey(row);
    if (!byMonth.has(key)) byMonth.set(key, { key, year: row.year, month: row.month, rows: [] });
    byMonth.get(key).rows.push(row);
  });
  return [...byMonth.values()]
    .sort((a, b) => b.key.localeCompare(a.key))
    .map((group) => ({
      ...group,
      rows: groupByCategory(group.rows),
      totals: sumTotals(group.rows),
      status: getMonthStatus(group.rows),
    }));
};

// Awaiting-returns rows are not saved by Refresh; they are closed from the row details instead.
export const hasAwaitingReturns = (rows) => rows.some((row) => row.statusCode === PNL_STATUS.AWAITING_RETURNS);

const isRefreshable = (row) => row.statusCode !== PNL_STATUS.FINAL && row.statusCode !== PNL_STATUS.AWAITING_RETURNS;

export const getOpenMonths = (rows) => {
  const open = new Map();
  rows
    .filter(isRefreshable)
    .forEach((row) => {
      const key = monthKey(row);
      if (!open.has(key)) {
        open.set(key, { key, year: row.year, month: row.month, label: monthLabel(row.year, row.month), categories: new Set() });
      }
      open.get(key).categories.add(getCategoryLabel(row.category));
    });
  return [...open.values()]
    .sort((a, b) => b.key.localeCompare(a.key))
    .map((month) => ({ ...month, categories: [...month.categories] }));
};

// Open months are sorted newest first, so the range runs from the last entry to the first.
export const getBackfillRange = (openMonths) => {
  if (!openMonths.length) return null;
  const newest = openMonths[0];
  const oldest = openMonths.at(-1);
  return { fromYear: oldest.year, fromMonth: oldest.month, toYear: newest.year, toMonth: newest.month };
};

export const getAdjustmentLines = (lines) => lines.filter((line) => toAmount(line.adjustment) !== 0);

const escapeCsvCell = (value) => {
  if (typeof value === "number") return value.toFixed(2);
  let text = String(value ?? "");
  // Neutralise spreadsheet formulas coming from free-text values.
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r']/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

export const buildCsv = (groups, totals) => {
  const header = ["Month", "Category", "Type", "Gross amount", "Net amount", "Status"];
  const body = groups.flatMap((group) =>
    group.rows.map((row) => [
      monthLabel(group.year, group.month),
      getCategoryLabel(row.category),
      row.subType,
      toAmount(row.grossAmount),
      toAmount(row.netAmount),
      getStatusInfo(row).label,
    ])
  );
  const grandTotal = ["Grand total", "", "", totals.gross, totals.net, ""];
  return [header, ...body, grandTotal].map((cells) => cells.map(escapeCsvCell).join(",")).join("\n");
};

export const buildCsvFilename = (filters) =>
  `pnl-summary-${filters.year}-${padMonth(filters.fromMonth)}-to-${padMonth(filters.toMonth)}.csv`;

export const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const LAST_REFRESHED_KEY = "pnlSummaryLastRefreshed";

// The API returns no refresh timestamp, so the last successful refresh is remembered per browser.
export const readLastRefreshed = () => {
  try {
    return localStorage.getItem(LAST_REFRESHED_KEY) || "";
  } catch {
    return "";
  }
};

export const saveLastRefreshed = (isoString) => {
  try {
    localStorage.setItem(LAST_REFRESHED_KEY, isoString);
  } catch {
    // Storage can be unavailable (private mode); the label just stays as it was.
  }
};

export const formatLastRefreshed = (isoString) =>
  isoString ? moment(isoString).format("DD MMM YYYY, hh:mm A") : "Not refreshed yet";
